import { Button, Col, Form, Row, Space, theme } from "antd";
import { useEffect, useState } from "react";
import { retry } from "../../utils/http";
import {
  mapAnswersQuery,
  AnswersColumns,
  AnswersSearchForm,
  AnswerStatus,
} from "./AnswersView.default";
import Data from "../../controls/Data";
import {
  getCompanies,
  getAnswers,
  getPlatformUsers,
  getQuestionnaires,
  getBundles,
} from "../../repository/data.repository";
import {
  ConditionLogic,
  QueryOperation,
  SearchQuery,
} from "../../code/search-query/search-query";
import { EmployeeUser } from "../../repository/data.default";
import { nameOf } from "../../utils/typescript";
import DynamicForm from "../../controls/DynamicForm";
import { useSelector } from "../../store/store";
import { authorizeSelectors } from "../../store/authorize";
import { createGridRequest } from "../../hooks/useGridRequest";
import { createSearchRequest } from "../../hooks/useSearchRequest";

export interface AnswersViewProps {
  readonly trigger?: string;
  readonly onSearch?: (searchQuery: SearchQuery) => void;
  readonly onAction?: (key: string, record: any) => void;
}

function AnswersView(props: AnswersViewProps) {
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const values = Form.useWatch<AnswersSearchForm>([], form);

  const [loading, setLoading] = useState(false);
  const [context, setContext] = useState<{
    readonly searchQuery: SearchQuery;
  }>({
    searchQuery: {
      conditions: [],
    },
  });
  const [valuesValid, setValuesValid] = useState(false);
  const permissions = useSelector(authorizeSelectors.permissions);

  // Lifecycle

  useEffect(() => {
    setContext({ ...context });
  }, [props.trigger]);

  useEffect(() => {
    setValuesValid(!form.getFieldsError().some(({ errors }) => errors.length));
  }, [values]);

  // Events

  function onLoading(loading: boolean): void {
    setLoading(loading);
  }

  function onSearchData(): void {
    const searchQuery = mapAnswersQuery(values);
    setContext({
      searchQuery,
    });

    props.onSearch?.(searchQuery);
  }

  function onClearData(): void {
    setValuesValid(true);
    form.resetFields();
    setContext({
      searchQuery: {
        conditions: [],
      },
    });
    props.onSearch?.({
      conditions: []
    })
  }

  function onAction(key: string, record: any): void {
    props.onAction?.(key, record);
  }

  // Methods

  const request = createGridRequest(getAnswers, { retry: true });
  const companiesRequest = createSearchRequest(getCompanies, "name");
  const bundlesRequest = createSearchRequest(getBundles, "name");
  const questionnairesRequest = createSearchRequest(getQuestionnaires, "name");

  // Matches what an operator knows about a person — first name, last name or username — and shows both the
  // username (what the grid lists) and the full name beneath it.
  const platformUsersRequest = (search: string) => {
    const searchQuery = {
      conditions: [
        ...( form.getFieldValue("company")?.value ? [{
          field: nameOf<EmployeeUser>("companyUid"),
          operation: QueryOperation.Equal,
          values: [form.getFieldValue("company")?.value],
      }] : []),
      ],
      groups: search
        ? [{
            logic: ConditionLogic.Or,
            conditions: (["firstName", "lastName", "userName"] as const).map((field) => ({
              field: nameOf<EmployeeUser>(field),
              operation: QueryOperation.StartsWith,
              values: [search],
            })),
          }]
        : [],
    };

    return retry(() =>
      getPlatformUsers({
        searchQuery,
        pageNumber: 0,
        pageSize: 100,
      }),
    ).then(({ data }) =>
      data.map((dataItem) => ({
        display: dataItem.userName,
        subDisplay: [`${dataItem.firstName ?? ""} ${dataItem.lastName ?? ""}`.trim(), dataItem.companyName]
          .filter(Boolean)
          .join(" · "),
        value: dataItem.uid,
      })),
    );
  };

  return (
    <Row className="flex flex-auto flex-row flex-nowrap overflow-hidden">
      {/* Form */}
      <Col
        className="mr-4 flex flex-col border border-solid pl-2 pr-2"
        style={{
          borderColor: token.colorBorder,
          borderRadius: token.borderRadius,
        }}
      >
        <Space className="flex flex-row justify-end pb-2 pt-2">
          <Button disabled={!valuesValid || loading} onClick={onClearData}>
            Clear
          </Button>
          <Button
            type="primary"
            loading={loading}
            disabled={!valuesValid}
            onClick={onSearchData}
          >
            Search
          </Button>
        </Space>
        <DynamicForm
          className="w-72 overflow-y-auto"
          form={form}
          schema={{
            groups: [
              {
                controls: [
                  {
                    type: "combobox",
                    name: "user",
                    label: "User Name",
                    request: platformUsersRequest,
                  },
                  {
                    type: "combobox",
                    name: "questionnaire",
                    label: "Questionnaire",
                    request: questionnairesRequest,
                  },
                  {
                    type: "combobox",
                    name: "bundle",
                    label: "Bundle",
                    request: bundlesRequest,
                  },
                  {
                    type: "combobox",
                    name: "company",
                    label: "Company",
                    request: companiesRequest,
                  },
                  {
                    type: "combobox",
                    name: "status",
                    label: "Status",
                    request: () =>
                      Promise.resolve(
                        Object.entries(AnswerStatus).map(([value, display]) => ({ display, value })),
                      ),
                  },
                  {
                    type: "rangetimepicker",
                    name: "createdOn",
                    label: "Started On",
                  },
                  {
                    type: "rangetimepicker",
                    name: "completedAt",
                    label: "Completed On",
                  },
                ],
              },
            ],
          }}
        />
      </Col>
      {/* Data */}
      <Data
        columns={AnswersColumns}
        context={context}
        pageSize={100}
        request={request}
        rowKey={({ uid }) => uid}
        onLoading={onLoading}
        actions={[
          {
            key: "view",
            label: "View",
            handler: onAction,
            disabled: !permissions.can("QuestionnairesAnswers", "view"),
          },
          {
            key: "delete",
            label: "Delete",
            danger: true,
            handler: onAction,
            disabled: !permissions.can("QuestionnairesAnswers", "delete"),
          },
        ]}
      />
    </Row>
  );
}

export default AnswersView;
